import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { conversationService, userService, customerService, commTemplateService, fileService, ticketService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { Badge } from "@/components/common/DataTable";
import { PROVIDER_COLOR } from "./ChannelsPage";
import { toast } from "sonner";
import {
  Send, Loader2, UserCircle2, Link2, Tag, StickyNote, Paperclip, X, Trash2, ExternalLink, FileText,
  Check, CheckCheck, AlertCircle, Clock, Ticket,
} from "lucide-react";

const PRIORITY_COLOR = { Low: "slate", Normal: "blue", High: "amber", Urgent: "red" };
const STATUSES = ["Open", "Pending", "Resolved", "Closed"];

export default function ConversationPanel({ conversationId, tenantId, onChange }) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showLink, setShowLink] = useState(false);
  const [uploading, setUploading] = useState(false);
  const threadRef = useRef(null);
  const fileRef = useRef(null);

  const canSend = hasPermission("MESSAGE.SEND");
  const canEdit = hasPermission("CONVERSATION.EDIT");
  const canAssign = hasPermission("CONVERSATION.ASSIGN");
  const canCreateTicket = hasPermission("TICKET.CREATE");

  const createTicket = async () => {
    if (conv.ticketId) return navigate(`/admin/tickets/${conv.ticketId}`);
    if (!conv.customerId) return toast.error("Link a customer to this conversation first");
    try {
      const t = await ticketService.create({
        subject: conv.subject || "Support request", priority: conv.priority || "Medium",
        customerId: conv.customerId, conversationId: conv.id, source: "inbox", tenantId: conv.tenantId,
      });
      toast.success(`Ticket ${t.ticketNumber} created`);
      navigate(`/admin/tickets/${t.id}`);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await conversationService.get(conversationId);
      setConv(c);
      const m = await conversationService.messages(conversationId);
      setMessages(m.items || []);
      if (c.unreadCount > 0) { await conversationService.markRead(conversationId); onChange?.(); }
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [conversationId, onChange]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const tid = conv?.tenantId || tenantId;
    if (tid) {
      userService.list({ tenantId: tid, size: 200 }).then((d) => setUsers(d.items || [])).catch(() => {});
      commTemplateService.list({ tenantId: tid }).then((d) => setTemplates(d.items || [])).catch(() => {});
    }
  }, [conv?.tenantId, tenantId]);
  useEffect(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; }, [messages]);

  if (loading) return <div className="flex-1 flex justify-center items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!conv) return <div className="flex-1 flex justify-center items-center text-muted-foreground">Conversation not found</div>;

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      if (conv.providerType === "whatsapp") {
        await conversationService.sendWhatsapp(conversationId, { body: draft });
      } else {
        await conversationService.sendMessage(conversationId, { body: draft, direction: "outbound" });
      }
      setDraft(""); await load(); onChange?.();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const patch = async (changes) => {
    try { const u = await conversationService.update(conversationId, changes); setConv({ ...conv, ...u }); onChange?.(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const isWhatsapp = conv.providerType === "whatsapp";

  const onAttachClick = () => {
    if (!isWhatsapp) return toast.info("Media sending is available on WhatsApp channels");
    fileRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("File exceeds 25MB limit");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "whatsapp");
      const uploaded = await fileService.upload(fd);
      await conversationService.sendWhatsapp(conversationId, { fileId: uploaded.id, caption: draft || undefined });
      setDraft(""); await load(); onChange?.();
      toast.success("Media sent");
    } catch (err) { toast.error(apiErrorMessage(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const assign = async (agentId) => {
    try { await conversationService.assign(conversationId, agentId || null); await load(); onChange?.(); toast.success(agentId ? "Assigned" : "Unassigned"); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div className="flex-1 flex min-w-0" data-testid="conversation-panel">
      {/* Thread column */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-semibold text-foreground truncate" data-testid="conv-subject">{conv.subject}</h2>
            <div className="flex items-center gap-2 mt-1">
              <Badge color={PROVIDER_COLOR[conv.providerType] || "slate"}>{conv.providerType}</Badge>
              <Badge color={PRIORITY_COLOR[conv.priority] || "slate"}>{conv.priority}</Badge>
              {conv.assignedTeam && <span className="text-xs text-muted-foreground">team: {conv.assignedTeam}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canCreateTicket && (
              <button onClick={createTicket} className="inline-flex items-center gap-1.5 border border-border rounded-lg px-2.5 py-1.5 text-sm text-foreground hover:bg-muted" data-testid="conv-create-ticket-btn" title={conv.ticketId ? "View linked ticket" : "Create ticket from conversation"}>
                <Ticket className="h-4 w-4" /> {conv.ticketId ? "View ticket" : "Create ticket"}
              </button>
            )}
            <select value={conv.status} onChange={(e) => patch({ status: e.target.value })} disabled={!canEdit}
              className="bg-card border border-border rounded-lg px-2.5 py-1.5 text-sm text-foreground" data-testid="conv-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div ref={threadRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-muted/20" data-testid="message-thread">
          {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">No messages yet</p>}
          {messages.map((m) => {
            const out = m.direction === "outbound";
            return (
              <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${out ? "bg-primary text-primary-foreground" : "bg-card border border-border text-foreground"}`}>
                  <p className="text-xs font-medium opacity-70 mb-0.5">{m.senderName}</p>
                  <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                  {(m.attachments || []).length > 0 && (
                    <div className="mt-1.5 space-y-1">
                      {m.attachments.map((a, i) => (
                        <div key={a.id || i} className="flex items-center gap-1.5 text-xs opacity-90 bg-black/10 rounded-md px-2 py-1">
                          <Paperclip className="h-3 w-3" /> <span className="truncate">{a.fileName || a.mediaType || "attachment"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-end gap-1 mt-1" data-testid={`message-status-${m.id}`}>
                    {out && <MessageStatus status={m.status} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-border p-3">
          {templates.length > 0 && canSend && (
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <select onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); if (t) setDraft(t.body); e.target.value = ""; }}
                className="bg-card border border-border rounded-lg px-2 py-1 text-xs text-foreground" data-testid="conv-template-select" defaultValue="">
                <option value="">Insert template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div className="flex items-end gap-2">
            <input ref={fileRef} type="file" className="hidden" onChange={onFileSelected} data-testid="conv-file-input"
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,video/mp4" />
            <button title={isWhatsapp ? "Send media (image/document)" : "Media available on WhatsApp"} onClick={onAttachClick}
              disabled={!canSend || uploading}
              className="p-2 rounded-lg border border-border text-muted-foreground hover:bg-muted disabled:opacity-50" data-testid="conv-attach-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            </button>
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} disabled={!canSend} rows={1}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={canSend ? (conv.providerType === "whatsapp" ? "Type a WhatsApp reply…" : "Type a reply… (mock send)") : "You don't have send permission"}
              className="flex-1 bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground resize-none outline-none focus:ring-2 focus:ring-primary/40" data-testid="conv-composer" />
            <button onClick={send} disabled={!canSend || sending || !draft.trim()} className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50" data-testid="conv-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Info column */}
      <div className="w-72 shrink-0 border-l border-border overflow-y-auto p-4 space-y-5" data-testid="conv-info">
        <Section icon={UserCircle2} title="Customer">
          {conv.customer ? (
            <div>
              <p className="text-sm font-medium text-foreground">{conv.customer.customerName}</p>
              <p className="text-xs text-muted-foreground">{conv.customer.customerCode}</p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => navigate(`/admin/customers/${conv.customer.id}`)} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="conv-open-customer"><ExternalLink className="h-3 w-3" /> Open</button>
                {canEdit && <button onClick={() => conversationService.linkCustomer(conversationId, null).then(load)} className="text-xs text-muted-foreground hover:text-destructive" data-testid="conv-unlink-customer">Unlink</button>}
              </div>
            </div>
          ) : (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Not linked to a customer</p>
              {canEdit && <button onClick={() => setShowLink(true)} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="conv-link-customer-btn"><Link2 className="h-3 w-3" /> Link customer</button>}
            </div>
          )}
        </Section>

        <Section icon={UserCircle2} title="Assigned Agent">
          {canAssign ? (
            <select value={conv.assignedAgentId || ""} onChange={(e) => assign(e.target.value)} className="ciq-input text-sm" data-testid="conv-assign-select">
              <option value="">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
            </select>
          ) : <p className="text-sm text-foreground">{conv.assignedAgentId || "Unassigned"}</p>}
        </Section>

        <TagsSection conv={conv} canEdit={canEdit} onSaved={load} />
        <NotesSection conversationId={conversationId} canEdit={canEdit} />
      </div>

      {showLink && <LinkCustomerModal conversationId={conversationId} tenantId={conv.tenantId} onClose={() => setShowLink(false)} onDone={() => { setShowLink(false); load(); }} />}
    </div>
  );
}

function MessageStatus({ status }) {
  const cls = "h-3.5 w-3.5";
  if (status === "read") return <span title="Read" className="flex items-center text-sky-300" data-testid="msg-read"><CheckCheck className={cls} /></span>;
  if (status === "delivered") return <span title="Delivered" className="flex items-center opacity-70" data-testid="msg-delivered"><CheckCheck className={cls} /></span>;
  if (status === "sent") return <span title="Sent" className="flex items-center opacity-70" data-testid="msg-sent"><Check className={cls} /></span>;
  if (status === "failed") return <span title="Failed" className="flex items-center text-red-300" data-testid="msg-failed"><AlertCircle className={cls} /></span>;
  return <span title={status} className="flex items-center opacity-60" data-testid="msg-queued"><Clock className={cls} /></span>;
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {title}</div>
      {children}
    </div>
  );
}

function TagsSection({ conv, canEdit, onSaved }) {
  const [tags, setTags] = useState(conv.tags || []);
  const [input, setInput] = useState("");
  useEffect(() => { setTags(conv.tags || []); }, [conv.tags]);
  const save = async (next) => {
    setTags(next);
    try { await conversationService.setTags(conv.id, next); onSaved?.(); } catch { /* noop */ }
  };
  const add = () => { const v = input.trim(); if (v && !tags.includes(v)) save([...tags, v]); setInput(""); };
  return (
    <Section icon={Tag} title="Tags">
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="conv-tags">
        {tags.length === 0 && <span className="text-xs text-muted-foreground">No tags</span>}
        {tags.map((tg) => (
          <span key={tg} className="inline-flex items-center gap-1 bg-muted rounded-full px-2 py-0.5 text-xs text-foreground">
            {tg}{canEdit && <button onClick={() => save(tags.filter((x) => x !== tg))} className="hover:text-destructive" data-testid={`conv-tag-remove-${tg}`}><X className="h-3 w-3" /></button>}
          </span>
        ))}
      </div>
      {canEdit && (
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add tag + Enter" className="ciq-input text-sm" data-testid="conv-tag-input" />
      )}
    </Section>
  );
}

function NotesSection({ conversationId, canEdit }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const refresh = useCallback(() => { conversationService.notes(conversationId).then((d) => setNotes(d.items || [])).catch(() => {}); }, [conversationId]);
  useEffect(() => { refresh(); }, [refresh]);
  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await conversationService.addNote(conversationId, text); setText(""); refresh(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { try { await conversationService.deleteNote(conversationId, id); refresh(); } catch { /* noop */ } };
  return (
    <Section icon={StickyNote} title="Internal Notes">
      {canEdit && (
        <div className="mb-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Add a private note…" className="ciq-input text-sm" data-testid="conv-note-input" />
          <button onClick={add} disabled={saving} className="mt-1 text-xs bg-primary text-primary-foreground rounded-md px-2.5 py-1 font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="conv-note-add">Add note</button>
        </div>
      )}
      <div className="space-y-2" data-testid="conv-notes-list">
        {notes.length === 0 && <span className="text-xs text-muted-foreground">No internal notes</span>}
        {notes.map((n) => (
          <div key={n.id} className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-2" data-testid={`conv-note-${n.id}`}>
            <p className="text-xs text-foreground whitespace-pre-wrap">{n.body}</p>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px] text-muted-foreground">{n.authorName}</span>
              {canEdit && <button onClick={() => del(n.id)} className="text-muted-foreground hover:text-destructive" data-testid={`conv-note-del-${n.id}`}><Trash2 className="h-3 w-3" /></button>}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function LinkCustomerModal({ conversationId, tenantId, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    const id = setTimeout(() => {
      customerService.list({ tenantId, q: q || undefined, size: 20 }).then((d) => setResults(d.items || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(id);
  }, [q, tenantId]);
  const pick = async (cid) => {
    try { await conversationService.linkCustomer(conversationId, cid); toast.success("Customer linked"); onDone(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="link-customer-modal">
        <h3 className="text-lg font-bold mb-4">Link Customer</h3>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="ciq-input mb-3" data-testid="link-customer-search" autoFocus />
        <div className="max-h-72 overflow-y-auto space-y-1">
          {results.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No customers found</p>}
          {results.map((c) => (
            <button key={c.id} onClick={() => pick(c.id)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted text-sm" data-testid={`link-customer-${c.id}`}>
              <span className="font-medium text-foreground">{c.customerName}</span>
              <span className="text-xs text-muted-foreground ml-2">{c.customerCode}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
